import React, { Fragment, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    Grid,
    Radio,
    RadioGroup,
    Tooltip,
    makeStyles,
} from '@material-ui/core';

import { showToast } from '@klpkonsern/ui';
import { useCurrentUser } from '@klp/auth';
import { ClientRoutes } from '@klp/client/config';
import { Conditional } from '@klp/conditional';
import { AppRoutes } from '@klp/core/app/config';
import { useAppParams } from '@klp/core/app/useAppParams';
import { ActionButton, Block, Heading } from '@klp/design';
import { ButtonsContainer } from '@klp/design/ButtonsContainer';
import { ErrorNotification } from '@klp/errorNotification';
import { Form, FormButton, FormInput, FormValidator } from '@klp/form';
import { Main } from '@klp/layout';
import { getDefaultLanguage } from '@klp/layout/Sidebar/config';

import { UserRegisterAccess, UserRoutes } from '../config';
import type { IRegisterUserRequest } from '../interfaces';
import { useRegisterUserMutation } from '../rtkApi';

const useStyles = makeStyles(() => ({
    buttonWrapper: {
        width: 'max-content',
        cursor: 'not-allowed',
    },
}));

export const RegisterUser: React.FC = () => {
    const { t } = useTranslation();
    const classes = useStyles();
    const { selectedClient } = useAppParams();
    const history = useHistory();

    const [registerUser, { isLoading, isSuccess, isError, error }] =
        useRegisterUserMutation();
    const { currentUser: user } = useCurrentUser();

    const [isSuperUser, setIsSuperUser] = useState(false);
    const [registrationFlag, setRegistrationFlag] = useState<
        boolean | undefined
    >(undefined);
    const [formValues, setFormValues] = useState<{
        email: string;
        firstName: string;
        lastName: string;
    }>({ email: '', firstName: '', lastName: '' });

    const handleCheckboxChange = () => {
        setIsSuperUser((prev) => !prev);
    };

    const handleRadioButtonChange = (checked: boolean) => {
        setRegistrationFlag(checked);
    };

    const onSubmit = async (
        event: React.FormEvent<HTMLFormElement>,
        values: IRegisterUserRequest,
    ) => {
        event.preventDefault();

        showToast({
            body: t('registerUser.registeringUser'),
            type: 'info',
        });

        const { email, firstName, lastName } = values;
        const culture = getDefaultLanguage();

        if (!selectedClient || registrationFlag === undefined) return;

        setFormValues({ email, firstName, lastName });

        const request = {
            email,
            firstName,
            lastName,
            customerNumber: selectedClient.kundeNummer,
            grantSuperUserPrivileges: isSuperUser,
            sendInvitation: registrationFlag,
            culture: culture,
        };

        try {
            await registerUser(request).unwrap();
            history;
        } catch (err) {
            // Error handled by useEffect
        }
    };

    useEffect(() => {
        if (!isSuccess || !selectedClient) return;

        showToast({
            body: t('registerUser.successNotification'),
            type: 'success',
        });
        history.push(UserRoutes.list());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccess]);

    useEffect(() => {
        if (isError) {
            showToast({
                body: t('registerUser.generalError.description'),
                type: 'error',
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isError]);

    return (
        <Main>
            <Conditional
                condition={UserRegisterAccess(user)}
                /* eslint-disable-next-line react/jsx-no-useless-fragment */
                errorComponent={<Fragment />}
            >
                <Conditional
                    condition={!!selectedClient}
                    errorComponent={
                        <ErrorNotification
                            title="clientSelect.errorNoClientsSelected.title"
                            description="clientSelect.errorNoClientsSelected.description"
                            backTarget={AppRoutes.mainPage()}
                            backTitle="general.goBackToFrontpage"
                            mainTarget={ClientRoutes.clientSelect()}
                            mainTitle="clientSelect.chooseClient"
                            returnUrl={true}
                        />
                    }
                >
                    {selectedClient && (
                        <Heading
                            klpHeadingLevel="THREE"
                            translation="registerUser.title"
                            translationOptions={{
                                customer: selectedClient?.kundeName,
                            }}
                        />
                    )}
                    {error && (
                        <ErrorNotification
                            type="Alert"
                            title="registerUser.generalError.title"
                            description="registerUser.generalError.description"
                        />
                    )}

                    <Form onSubmit={onSubmit}>
                        <Grid container={true} spacing={2}>
                            <Grid item={true} xs={12}>
                                <FormInput
                                    name="email"
                                    title={t('registerUser.email')}
                                    validators={[
                                        new FormValidator(
                                            FormValidator.validations.required,
                                            t('validator.required'),
                                        ),
                                        new FormValidator(
                                            FormValidator.validations.isEmail,
                                            t('validator.email'),
                                        ),
                                    ]}
                                    value={formValues.email}
                                    required={true}
                                />
                            </Grid>
                            <Grid item={true} xs={12} sm={6}>
                                <FormInput
                                    name="firstName"
                                    title={t('registerUser.firstName')}
                                    validators={[
                                        new FormValidator(
                                            FormValidator.validations.required,
                                            t('validator.required'),
                                        ),
                                    ]}
                                    value={formValues.firstName}
                                    required={true}
                                />
                            </Grid>
                            <Grid item={true} xs={12} sm={6}>
                                <FormInput
                                    name="lastName"
                                    title={t('registerUser.lastName')}
                                    validators={[
                                        new FormValidator(
                                            FormValidator.validations.required,
                                            t('validator.required'),
                                        ),
                                    ]}
                                    value={formValues.lastName}
                                    required={true}
                                />
                            </Grid>
                            <Grid item={true} xs={12} sm={6}>
                                <Block paddingTop="compact">
                                    <Heading
                                        klpHeadingLevel="FOUR"
                                        translation="registerUser.roles.title"
                                    />
                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={isSuperUser}
                                                    onChange={
                                                        handleCheckboxChange
                                                    }
                                                />
                                            }
                                            label={t(
                                                'registerUser.roles.superUserCheckBox',
                                            )}
                                        />
                                    </FormGroup>
                                </Block>
                            </Grid>
                            <Grid item={true} xs={12} sm={6}>
                                <Block paddingTop="compact">
                                    <Heading
                                        klpHeadingLevel="FOUR"
                                        translation="registerUser.other.title"
                                    />
                                    <Block paddingTop="tiny">
                                        <Heading
                                            klpHeadingLevel="FIVE"
                                            klpColor="dark"
                                            translation="registerUser.other.velkomstmelding"
                                        />
                                    </Block>
                                    <Block paddingTop="compact">
                                        <FormControl>
                                            <RadioGroup
                                                row={true}
                                                aria-labelledby="demo-row-radio-buttons-group-label"
                                                name="row-radio-buttons-group"
                                            >
                                                <FormControlLabel
                                                    value={
                                                        registrationFlag ===
                                                        false
                                                    }
                                                    checked={
                                                        registrationFlag ===
                                                        false
                                                    }
                                                    control={<Radio />}
                                                    label={t(
                                                        'registerUser.other.noButton',
                                                    )}
                                                    onClick={() =>
                                                        handleRadioButtonChange(
                                                            false,
                                                        )
                                                    }
                                                />
                                                <Block paddingLeft="compact">
                                                    <FormControlLabel
                                                        value={
                                                            registrationFlag ===
                                                            true
                                                        }
                                                        checked={
                                                            registrationFlag ===
                                                            true
                                                        }
                                                        control={<Radio />}
                                                        label={t(
                                                            'registerUser.other.yesButton',
                                                        )}
                                                        onClick={() =>
                                                            handleRadioButtonChange(
                                                                true,
                                                            )
                                                        }
                                                    />
                                                </Block>
                                            </RadioGroup>
                                        </FormControl>
                                    </Block>
                                </Block>
                            </Grid>
                            <Grid item={true} xs={12}>
                                <ButtonsContainer>
                                    <ActionButton
                                        actionType="cancel"
                                        translation="general.cancel"
                                        target={UserRoutes.list()}
                                    />

                                    {!registrationFlag && (
                                        <Tooltip
                                            title={t(
                                                'registerUser.disabledTooltip',
                                            )}
                                        >
                                            <Block
                                                paddingLeft="tiny"
                                                className={
                                                    classes.buttonWrapper
                                                }
                                            >
                                                <FormButton
                                                    disabled={
                                                        registrationFlag ===
                                                        undefined
                                                    }
                                                    name="Submit"
                                                    actionType="submit"
                                                    value={t('general.submit')}
                                                    disableOnError={true}
                                                    isLoading={isLoading}
                                                    translation="general.submit"
                                                />
                                            </Block>
                                        </Tooltip>
                                    )}
                                    {registrationFlag && (
                                        <FormButton
                                            name="Submit"
                                            actionType="submit"
                                            value={t('general.submit')}
                                            disableOnError={true}
                                            isLoading={isLoading}
                                            translation="general.submit"
                                        />
                                    )}
                                </ButtonsContainer>
                            </Grid>
                        </Grid>
                    </Form>
                </Conditional>
            </Conditional>
        </Main>
    );
};
